import { useCallback, useEffect, useRef, useState } from "react";
import type { Timestamp } from "firebase/firestore";

import type { CaptureMetadata } from "@shared/types/annotations";
import { loadDepthFrameResult } from "@/lib/measurement/depth3d";
import {
  classifyDepthEvidence,
  isRetryableDepthFrameFailure,
  type DepthAvailability,
} from "@/lib/measurement/depthAvailability";

/**
 * Resolve what a capture's depth evidence allows the measurement editor to do,
 * fetching the depth frame only when the metadata and the entry agree there is
 * one. Failures keep their cause so the UI can offer a retry for the transient
 * ones (a network blip, a metadata fetch) and lock editing for the rest.
 *
 * "The capture describes depth but the entry recorded no depth file" is treated
 * as transient EXACTLY ONCE: an entry mid-upload resolves itself, while a stuck
 * one must not hand the user an infinite retry button.
 */
export function useDepthAvailability({
  enabled,
  captureMetadata,
  metadataFailed,
  metadataPath,
  entryDepthCloudPath,
  updatedAt,
  onMetadataInvalidated,
}: {
  /** Only edit mode on a segmentation capture needs (or is allowed to fetch) depth. */
  enabled: boolean;
  captureMetadata: CaptureMetadata | null;
  metadataFailed: boolean;
  metadataPath?: string;
  entryDepthCloudPath?: string | null;
  updatedAt?: Timestamp;
  /**
   * Called when a retry needs the capture metadata re-read. The owner of the
   * metadata (and its cache) drops the cached entry and refetches; the hook only
   * decides WHICH retries are worth a re-read, so the caller never double-fetches
   * (and so double-downloads the depth) on a plain depth retry.
   */
  onMetadataInvalidated?: () => void;
}): { availability: DepthAvailability | null; retry: () => void } {
  const [availability, setAvailabilityState] = useState<DepthAvailability | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  // Read by retry() so its identity does not churn with every status change.
  const availabilityRef = useRef<DepthAvailability | null>(null);
  const expectedIncompleteRetriedRef = useRef(false);

  const setAvailability = useCallback((next: DepthAvailability | null) => {
    availabilityRef.current = next;
    setAvailabilityState(next);
  }, []);

  // A different capture (or a re-uploaded one) earns its single retry back.
  // Declared first so the reset lands before the classification effect below.
  useEffect(() => {
    expectedIncompleteRetriedRef.current = false;
  }, [metadataPath, updatedAt]);

  useEffect(() => {
    if (!enabled) {
      setAvailability(null);
      return;
    }

    const evidence = classifyDepthEvidence({ captureMetadata, metadataFailed, metadataPath, entryDepthCloudPath });
    switch (evidence.decision) {
      case "wait":
        setAvailability({ status: "metadata-loading" });
        return;
      case "metadata-error":
        setAvailability({ status: "metadata-error", cause: "metadata-fetch-failed" });
        return;
      case "absent":
        setAvailability({ status: "absent" });
        return;
      case "expected-incomplete":
        setAvailability({
          status: expectedIncompleteRetriedRef.current ? "terminal-error" : "retryable-error",
          cause: "entry-depth-path-missing",
        });
        return;
      case "inconsistent":
        setAvailability({ status: "terminal-error", cause: "entry-depth-path-orphaned" });
        return;
      case "load": {
        const { depthCloudPath } = evidence;
        let cancelled = false;
        setAvailability({ status: "loading", depthCloudPath });
        const version = updatedAt ? String(updatedAt.toMillis()) : undefined;
        loadDepthFrameResult(depthCloudPath, captureMetadata!, version)
          .then((result) => {
            if (cancelled) return;
            if (result.ok) {
              setAvailability({ status: "ready", frame: result.frame });
              return;
            }
            setAvailability({
              status: isRetryableDepthFrameFailure(result.cause) ? "retryable-error" : "terminal-error",
              cause: result.cause,
            });
          })
          .catch((error) => {
            if (cancelled) return;
            console.warn("useDepthAvailability: depth frame load threw", error);
            setAvailability({ status: "terminal-error", cause: "frame-invalid" });
          });
        return () => {
          cancelled = true;
        };
      }
    }
  }, [
    captureMetadata,
    enabled,
    entryDepthCloudPath,
    metadataFailed,
    metadataPath,
    retryNonce,
    setAvailability,
    updatedAt,
  ]);

  const retry = useCallback(() => {
    const current = availabilityRef.current;
    if (!current || (current.status !== "metadata-error" && current.status !== "retryable-error")) return;

    // Spend the single expected-incomplete retry, and re-read the metadata in
    // case the capture was re-uploaded with its depth descriptor since.
    const isExpectedIncomplete =
      current.status === "retryable-error" && current.cause === "entry-depth-path-missing";
    if (isExpectedIncomplete) expectedIncompleteRetriedRef.current = true;
    if (current.status === "metadata-error" || isExpectedIncomplete) onMetadataInvalidated?.();
    // A failed depth fetch self-evicts from the depth cache, so the bumped nonce
    // is enough to make the effect download it again.
    setRetryNonce((nonce) => nonce + 1);
  }, [onMetadataInvalidated]);

  return { availability, retry };
}
